import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sparkles, AlertCircle, CheckCircle, Loader2, Globe, Lock, ArrowLeft } from 'lucide-react';
import { taskService } from '@/core/api';
import { useTelegram } from '@/core/providers';
import { LoadingSpinner } from '@/components';
import type { TrackingIntent, Task } from '@/types';

const EXAMPLES = [
  'Track Bitcoin price movements',
  'Monitor Elon Musk on Twitter',
  'Follow AI industry news',
  'Watch Ethereum below $2000',
];

// All available data sources
const ALL_AVAILABLE_SOURCES = [
  'twitter',
  'news',
  'weixin',
  'rss',
  'youtube',
  'github',
  'medium',
  'podcast',
  'producthunt',
  'reddit',
];

type Step = 'input' | 'preview' | 'creating' | 'completed' | 'error';

function CreateTaskPage() {
  const navigate = useNavigate();
  const { hapticFeedback } = useTelegram();

  // State
  const [step, setStep] = useState<Step>('input');
  const [input, setInput] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [parsedIntent, setParsedIntent] = useState<TrackingIntent | null>(null);
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [createdTask, setCreatedTask] = useState<Task | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isParsingIntent, setIsParsingIntent] = useState(false);

  const handleParseIntent = async () => {
    if (!input.trim()) return;

    setIsParsingIntent(true);
    setError(null);
    hapticFeedback('impact');

    try {
      const intent = await taskService.parseIntent(input);
      if (intent && intent.type) {
        setParsedIntent(intent);
        // Initialize selected sources with AI recommendations
        setSelectedSources(intent.sources || []);
        setStep('preview');
        hapticFeedback('notification');
      } else {
        setError('Failed to parse your request. Please try rephrasing it.');
      }
    } catch (err: any) {
      console.error('Parse error:', err);

      if (err.response?.status === 429) {
        setError('⏳ AI service is busy. Please wait a moment and try again.');
      } else {
        const errorMessage = err.response?.data?.error || 'Failed to parse. Please try again.';
        setError(errorMessage);
      }
    } finally {
      setIsParsingIntent(false);
    }
  };

  const handleCreateTask = async () => {
    if (!parsedIntent) return;

    setStep('creating');
    setError(null);
    hapticFeedback('impact');

    try {
      // Backend now returns task immediately with status='pending'
      const task = await taskService.createFromIntent(input, isPublic, selectedSources);
      setCreatedTask(task);
      setStep('completed');
      hapticFeedback('notification');

      // Navigate to task detail after 2 seconds
      setTimeout(() => {
        navigate(`/tasks/${task.id}`);
      }, 2000);
    } catch (err: any) {
      console.error('Create error:', err);
      const errorMessage = err.response?.data?.error || 'Failed to create task';
      setError(errorMessage);
      setStep('error');
      hapticFeedback('notification');
    }
  };

  const handleReset = () => {
    setStep('input');
    setInput('');
    setParsedIntent(null);
    setCreatedTask(null);
    setError(null);
    setIsPublic(false);
    hapticFeedback('selection');
  };

  const handleExampleClick = (example: string) => {
    hapticFeedback('selection');
    setInput(example);
    setError(null);
  };

  const handleEditIntent = () => {
    setStep('input');
    hapticFeedback('selection');
  };

  return (
    <div className="h-full flex flex-col bg-telegram-bg">
      <div
        className="pointer-events-none fixed left-1/2 z-30 -translate-x-1/2"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <div className="pointer-events-auto flex items-center gap-2 rounded-full bg-[var(--ff-surface)]/90 px-2 py-1 shadow-[0_12px_28px_rgba(0,0,0,0.08)] backdrop-blur">
          <button
            onClick={() => navigate(-1)}
            className="rounded-full border border-[var(--ff-border)] bg-[var(--ff-surface-alt)] px-3 py-2 text-sm font-semibold text-telegram-text shadow-sm hover:shadow transition"
            aria-label="Go back"
          >
            <ArrowLeft size={18} />
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="p-4 pt-20 space-y-4">
          {/* Input Step */}
          {step === 'input' && (
            <>
              <div className="space-y-2">
                <textarea
                  value={input}
                  onChange={(e) => {
                    setInput(e.target.value);
                    setError(null);
                  }}
                  placeholder="e.g., Track Bitcoin when it drops below $30,000"
                  className="w-full h-32 px-4 py-3 bg-telegram-secondary rounded-xl text-telegram-text placeholder-telegram-hint resize-none focus:outline-none focus:ring-2 focus:ring-telegram-button"
                  autoFocus
                />

                <button
                  onClick={handleParseIntent}
                  disabled={!input.trim() || isParsingIntent}
                  className="w-full btn-primary flex items-center justify-center gap-2"
                >
                  {isParsingIntent ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <>
                      <Sparkles size={16} />
                      <span>Analyze Intent</span>
                    </>
                  )}
                </button>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                  <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              <div className="space-y-2">
                <h3 className="text-sm font-medium text-telegram-hint">Examples</h3>
                <div className="space-y-2">
                  {EXAMPLES.map((example, index) => (
                    <button
                      key={index}
                      onClick={() => handleExampleClick(example)}
                      className="w-full text-left p-3 bg-telegram-secondary rounded-lg text-sm text-telegram-text hover:bg-telegram-hint/10 transition-colors"
                    >
                      {example}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Preview Step */}
          {step === 'preview' && parsedIntent && (
            <div className="space-y-4 animate-slide-up">
              {/* Intent Card */}
              <div className="bg-telegram-secondary rounded-xl p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <span className="text-3xl">{parsedIntent.icon}</span>
                  <div className="flex-1">
                    <h3 className="font-medium text-telegram-text">{parsedIntent.display_name}</h3>
                    <p className="text-sm text-telegram-hint mt-1">{parsedIntent.description}</p>
                  </div>
                </div>

                {/* Confidence */}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-telegram-hint">Confidence</span>
                  <div className="flex items-center gap-2">
                    <div className="w-24 h-2 bg-telegram-bg rounded-full overflow-hidden">
                      <div
                        className="h-full bg-telegram-button rounded-full transition-all"
                        style={{ width: `${parsedIntent.confidence * 100}%` }}
                      />
                    </div>
                    <span className="text-sm text-telegram-text font-medium">
                      {(parsedIntent.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>

                {/* Type and Target */}
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-telegram-hint">Type</span>
                    <span className="text-telegram-text font-medium">{parsedIntent.type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-telegram-hint">Target</span>
                    <span className="text-telegram-text font-medium">{parsedIntent.target}</span>
                  </div>
                </div>

                {/* Keywords */}
                {parsedIntent.keywords.length > 0 && (
                  <div>
                    <p className="text-sm text-telegram-hint mb-2">Keywords</p>
                    <div className="flex flex-wrap gap-1.5">
                      {parsedIntent.keywords.map((kw, idx) => (
                        <span
                          key={idx}
                          className="px-2.5 py-1 bg-telegram-bg text-telegram-text text-xs rounded-full border border-telegram-secondary"
                        >
                          {kw}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sources - All Available Sources with AI Recommendations */}
                <div>
                  <p className="text-sm text-telegram-hint mb-2">
                    Data Sources (tap to toggle, ✨ = AI recommended)
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {ALL_AVAILABLE_SOURCES.map((source, idx) => {
                      const isSelected = selectedSources.includes(source);
                      const isAiRecommended = parsedIntent.sources.includes(source);
                      return (
                        <button
                          key={idx}
                          onClick={() => {
                            hapticFeedback('selection');
                            setSelectedSources(prev =>
                              isSelected
                                ? prev.filter(s => s !== source)
                                : [...prev, source]
                            );
                          }}
                          className={`px-2.5 py-1 text-xs rounded-full font-medium transition-all ${
                            isSelected
                              ? 'bg-telegram-button text-white shadow-sm'
                              : 'bg-telegram-hint/20 text-telegram-hint border border-telegram-hint/30'
                          }`}
                        >
                          {isAiRecommended && '✨ '}
                          {source}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-telegram-hint mt-2">
                    {selectedSources.length === 0
                      ? '⚠️ Select at least one source'
                      : `✓ ${selectedSources.length} source${selectedSources.length > 1 ? 's' : ''} selected`}
                  </p>
                </div>

                {/* Instructions */}
                {parsedIntent.instructions.length > 0 && (
                  <div>
                    <p className="text-sm text-telegram-hint mb-2">How it works</p>
                    <div className="space-y-1.5">
                      {parsedIntent.instructions.map((instruction, idx) => (
                        <div key={idx} className="flex gap-2 text-xs text-telegram-text">
                          <span className="text-telegram-hint">{idx + 1}.</span>
                          <span>{instruction}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Public/Private Toggle */}
              <div className="bg-telegram-secondary rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {isPublic ? (
                      <Globe size={20} className="text-telegram-button" />
                    ) : (
                      <Lock size={20} className="text-telegram-hint" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-telegram-text">
                        {isPublic ? 'Public Task' : 'Private Task'}
                      </p>
                      <p className="text-xs text-telegram-hint mt-0.5">
                        {isPublic
                          ? 'Others can discover and subscribe to this task'
                          : 'Only you can see this task'
                        }
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setIsPublic(!isPublic);
                      hapticFeedback('selection');
                    }}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      isPublic ? 'bg-telegram-button' : 'bg-telegram-hint/30'
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                        isPublic ? 'left-5' : 'left-0.5'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={handleEditIntent}
                  className="flex-1 btn-secondary"
                >
                  Edit
                </button>
                <button
                  onClick={handleCreateTask}
                  disabled={selectedSources.length === 0}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Create Task
                </button>
              </div>
            </div>
          )}

          {/* Creating Step */}
          {step === 'creating' && (
            <div className="space-y-4 animate-slide-up">
              <div className="bg-telegram-secondary rounded-xl p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="relative">
                    <Loader2 size={48} className="text-telegram-button animate-spin" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-telegram-text">
                      Creating your tracking task...
                    </h3>
                    <p className="text-sm text-telegram-hint mt-2">
                      Just a moment
                    </p>
                  </div>
                  <div className="w-full max-w-xs h-1 bg-telegram-bg rounded-full overflow-hidden">
                    <div className="h-full bg-telegram-button rounded-full animate-pulse" style={{ width: '70%' }} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Completed Step */}
          {step === 'completed' && createdTask && (
            <div className="space-y-4 animate-slide-up">
              <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <CheckCircle size={48} className="text-green-500" />
                  <div>
                    <h3 className="text-lg font-medium text-telegram-text">Task Created!</h3>
                    <p className="text-sm text-telegram-hint mt-2">
                      Your tracking task "{createdTask.title || parsedIntent?.display_name}" is being set up
                    </p>
                    <p className="text-xs text-telegram-hint mt-1">
                      Status: {createdTask.status}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Error Step */}
          {step === 'error' && (
            <div className="space-y-4 animate-slide-up">
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <AlertCircle size={48} className="text-red-500" />
                  <div>
                    <h3 className="text-lg font-medium text-telegram-text">Creation Failed</h3>
                    <p className="text-sm text-red-600 dark:text-red-400 mt-2">
                      {error || 'Failed to create tracking task'}
                    </p>
                  </div>
                  <button onClick={handleReset} className="btn-primary">
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreateTaskPage;
